import React, { useState } from 'react';
import {
  AlertTriangle, ChevronRight, ChevronLeft, Check, X,
  User, MapPin, Clock, Shield, Loader2, FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const categories = ['Fall', 'Medication Error', 'Pressure Injury', 'Infection', 'Near Miss', 'Equipment Failure', 'Security', 'Patient Complaint', 'Other'];
const severities = ['low', 'medium', 'high', 'critical'];
const harmLevels = [
  { value: 'no_harm', label: 'No Harm', desc: 'Event reached patient but caused no harm', color: 'border-green-200 bg-green-50' },
  { value: 'near_miss', label: 'Near Miss', desc: 'Event did not reach the patient', color: 'border-yellow-200 bg-yellow-50' },
  { value: 'temporary_minor', label: 'Temporary Minor Harm', desc: 'Required monitoring or minor intervention', color: 'border-orange-200 bg-orange-50' },
  { value: 'temporary_major', label: 'Temporary Major Harm', desc: 'Required intervention, initial or prolonged hospitalization', color: 'border-red-200 bg-red-50' },
  { value: 'permanent_harm', label: 'Permanent Harm', desc: 'Resulted in permanent patient harm', color: 'border-red-300 bg-red-100' },
  { value: 'death', label: 'Death', desc: 'Event contributed to patient death', color: 'border-gray-800 bg-gray-100' },
];
const contributingFactors = ['Communication', 'Environment', 'Equipment', 'Human Factors', 'Medication', 'Patient Factors', 'Policy/Procedure', 'Staffing', 'System Failure', 'Training', 'Supervision', 'Fatigue'];
const discoveryMethods = ['Self-Reported', 'Witnessed', 'Patient/Family Report', 'Chart Review', 'Surveillance System', 'Rounding/Inspection', 'Other'];
const departments = ['ICU', 'MICU', 'SICU', 'Emergency', 'Medical-Surgical', 'Pediatrics', 'OB/GYN', 'Oncology', 'Pharmacy', 'Radiology', 'Lab', 'OR', 'PACU', 'Other'];

const steps = [
  { id: 1, title: 'Event Type', icon: AlertTriangle },
  { id: 2, title: 'Details', icon: FileText },
  { id: 3, title: 'Harm Assessment', icon: Shield },
  { id: 4, title: 'Contributing Factors', icon: User },
  { id: 5, title: 'Review & Submit', icon: Check },
];

const ReportEventPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '', category: '', severity: '', description: '',
    location: '', department: '', discovery_method: '',
    time_of_incident: '', patient_involved: false, patient_harm: false,
    harm_level: '', patient_age: '', patient_gender: '',
    contributing_factors: [], witnesses: '', immediate_actions: '',
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const toggleFactor = (f) => {
    set('contributing_factors', form.contributing_factors.includes(f)
      ? form.contributing_factors.filter(x => x !== f)
      : [...form.contributing_factors, f]
    );
  };

  // Auto-suggest severity based on harm level
  const autoSeverity = (harmLevel) => {
    set('harm_level', harmLevel);
    if (['death', 'permanent_harm'].includes(harmLevel)) set('severity', 'critical');
    else if (harmLevel === 'temporary_major') set('severity', 'high');
    else if (harmLevel === 'temporary_minor') set('severity', 'medium');
    else if (['near_miss', 'no_harm'].includes(harmLevel)) set('severity', form.severity || 'low');
  };

  const canNext = () => {
    if (step === 1) return form.title && form.category;
    if (step === 2) return form.description && form.location;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        severity: form.severity || 'medium',
        witnesses: form.witnesses ? form.witnesses.split(',').map(w => w.trim()).filter(Boolean) : [],
      };
      await api.post('/api/safety-events', payload);
      toast.success('Safety event reported successfully');
      navigate('/hospital/safety-events');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="report-event-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Report Safety Event</h1>
        <p className="text-gray-500 mt-1">Guided incident reporting wizard</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center mb-8 overflow-x-auto pb-2">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              onClick={() => s.id < step && setStep(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                s.id === step ? 'bg-[var(--niko-teal)] text-white' :
                s.id < step ? 'bg-green-100 text-green-700 cursor-pointer' :
                'bg-gray-100 text-gray-400'
              }`}
              data-testid={`step-${s.id}`}
            >
              <s.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{s.title}</span>
              <span className="sm:hidden">{s.id}</span>
            </button>
            {i < steps.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 mx-1 flex-shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        {/* Step 1: Event Type */}
        {step === 1 && (
          <div className="p-6 space-y-5" data-testid="step-1-content">
            <div>
              <Label className="text-sm font-medium">Event Title *</Label>
              <Input className="mt-1.5" placeholder="Brief description of what happened..." value={form.title} onChange={e => set('title', e.target.value)} data-testid="event-title-input" />
            </div>
            <div>
              <Label className="text-sm font-medium">Event Category *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {categories.map(cat => (
                  <button key={cat} onClick={() => set('category', cat)}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                      form.category === cat ? 'border-[var(--niko-teal)] bg-[var(--niko-teal)]/5 text-[var(--niko-teal)]' : 'border-gray-100 text-gray-600 hover:border-gray-200'
                    }`}
                    data-testid={`category-${cat}`}
                  >{cat}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">How was this discovered?</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {discoveryMethods.map(dm => (
                  <button key={dm} onClick={() => set('discovery_method', dm)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${
                      form.discovery_method === dm ? 'border-[var(--niko-teal)] bg-[var(--niko-teal)]/5 text-[var(--niko-teal)]' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >{dm}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="p-6 space-y-5" data-testid="step-2-content">
            <div><Label>Description *</Label><Textarea className="mt-1.5" rows={4} placeholder="Detailed description of the event..." value={form.description} onChange={e => set('description', e.target.value)} data-testid="event-description-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <select className="w-full mt-1.5 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.department} onChange={e => set('department', e.target.value)}>
                  <option value="">Select...</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div><Label>Location *</Label><Input className="mt-1.5" placeholder="e.g., ICU Room 204" value={form.location} onChange={e => set('location', e.target.value)} data-testid="event-location-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Time of Incident</Label><Input type="time" className="mt-1.5" value={form.time_of_incident} onChange={e => set('time_of_incident', e.target.value)} /></div>
              <div><Label>Witnesses (comma separated)</Label><Input className="mt-1.5" placeholder="Nurse Smith, Dr. Jones" value={form.witnesses} onChange={e => set('witnesses', e.target.value)} /></div>
            </div>
            <div><Label>Immediate Actions Taken</Label><Textarea className="mt-1.5" rows={2} placeholder="What was done immediately..." value={form.immediate_actions} onChange={e => set('immediate_actions', e.target.value)} /></div>
          </div>
        )}

        {/* Step 3: Harm Assessment */}
        {step === 3 && (
          <div className="p-6 space-y-5" data-testid="step-3-content">
            <div className="flex items-center gap-6 mb-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={form.patient_involved} onChange={e => set('patient_involved', e.target.checked)} className="rounded w-4 h-4 text-[var(--niko-teal)]" />
                Patient was involved
              </label>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={form.patient_harm} onChange={e => set('patient_harm', e.target.checked)} className="rounded w-4 h-4 text-red-500" />
                Patient harm occurred
              </label>
            </div>
            {form.patient_involved && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                <div><Label>Patient Age</Label><Input className="mt-1" placeholder="e.g., 72" value={form.patient_age} onChange={e => set('patient_age', e.target.value)} /></div>
                <div>
                  <Label>Patient Gender</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.patient_gender} onChange={e => set('patient_gender', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium mb-2 block">Harm Level Classification (NCC MERP)</Label>
              <p className="text-xs text-gray-400 mb-3">Selecting a harm level will auto-suggest severity</p>
              <div className="space-y-2">
                {harmLevels.map(hl => (
                  <button key={hl.value} onClick={() => autoSeverity(hl.value)}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                      form.harm_level === hl.value ? hl.color + ' border-opacity-100' : 'border-gray-100 hover:border-gray-200'
                    }`}
                    data-testid={`harm-${hl.value}`}
                  >
                    <span className="font-medium text-sm text-gray-900">{hl.label}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{hl.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Auto-Suggested Severity</Label>
              <div className="flex gap-2 mt-2">
                {severities.map(s => (
                  <button key={s} onClick={() => set('severity', s)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize border-2 transition-all ${
                      form.severity === s
                        ? s === 'critical' ? 'border-red-500 bg-red-50 text-red-700' :
                          s === 'high' ? 'border-orange-500 bg-orange-50 text-orange-700' :
                          s === 'medium' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' :
                          'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-100 text-gray-500'
                    }`}
                    data-testid={`severity-${s}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Contributing Factors */}
        {step === 4 && (
          <div className="p-6 space-y-5" data-testid="step-4-content">
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Contributing Factors (AHRQ Taxonomy)</Label>
              <p className="text-xs text-gray-400 mb-3">Select all that apply to help identify root causes</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {contributingFactors.map(f => (
                  <button key={f} onClick={() => toggleFactor(f)}
                    className={`px-3 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                      form.contributing_factors.includes(f) ? 'border-[var(--niko-teal)] bg-[var(--niko-teal)]/5 text-[var(--niko-teal)]' : 'border-gray-100 text-gray-600 hover:border-gray-200'
                    }`}
                    data-testid={`factor-${f}`}
                  >
                    {form.contributing_factors.includes(f) && <Check className="w-3 h-3 inline mr-1" />}
                    {f}
                  </button>
                ))}
              </div>
            </div>
            {form.contributing_factors.length > 0 && (
              <div className="p-3 bg-[var(--niko-teal)]/5 rounded-xl">
                <p className="text-sm text-[var(--niko-teal)] font-medium">
                  {form.contributing_factors.length} factor{form.contributing_factors.length > 1 ? 's' : ''} selected: {form.contributing_factors.join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Review & Submit */}
        {step === 5 && (
          <div className="p-6 space-y-4" data-testid="step-5-content">
            <h3 className="font-semibold text-gray-900">Review Your Report</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Title', value: form.title },
                { label: 'Category', value: form.category },
                { label: 'Severity', value: form.severity || 'Not set' },
                { label: 'Harm Level', value: harmLevels.find(h => h.value === form.harm_level)?.label || 'Not classified' },
                { label: 'Location', value: form.location },
                { label: 'Department', value: form.department || 'Not specified' },
                { label: 'Discovery', value: form.discovery_method || 'Not specified' },
                { label: 'Patient Involved', value: form.patient_involved ? 'Yes' : 'No' },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{value}</p>
                </div>
              ))}
            </div>
            {form.description && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Description</p>
                <p className="text-sm text-gray-700 mt-1">{form.description}</p>
              </div>
            )}
            {form.contributing_factors.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Contributing Factors</p>
                <div className="flex flex-wrap gap-1">
                  {form.contributing_factors.map(f => (
                    <span key={f} className="px-2 py-0.5 bg-[var(--niko-teal)]/10 text-[var(--niko-teal)] rounded text-xs font-medium">{f}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="gap-2" data-testid="prev-step-btn">
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/hospital/safety-events')} data-testid="cancel-report-btn">Cancel</Button>
            {step < 5 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canNext()} className="btn-primary gap-2" data-testid="next-step-btn">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="submit-report-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                Submit Report
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportEventPage;
